import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { NgSelectConfig } from '@ng-select/ng-select';
import { firstValueFrom, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { compiledVersion as compiledVersionData } from '../compiled-version';
import { Toolbox } from './classes/toolbox';
import { User } from './classes/user.class';
import { CodaltComponent } from './codalt.component';
import { ToolboxDialogComponent } from './pages/toolbox/toolbox-dialog/toolbox-dialog.component';
import { ApiErrorResponse, ApiService } from './services/api/api.service';
import { AuthService } from './services/auth/auth.service';
import { AuthorisationService } from './services/auth/authorisation.service';
import { ClientDeviceService } from './services/client-device.service';
import { ConfirmDialogService } from './services/confirm-dialog-service/confirm-dialog.service';
import { DialogEscapeService } from './services/dialog/dialog-escape.service';
import { EntityTypeService } from './services/entity-type.service';
import { ToolboxService } from './services/toolbox.service';
import { WebsocketService } from './services/websocket/websocket.service';
import { LocalStorage } from './storage.class';
import { Utils } from './utils.class';

const TOOLBOX_LOAD_DELAY_MS = 1000 as const;
/** 5 minutes in milliseconden */
const VERSION_CHECK_INTERVAL_MS = 300000 as const;
const CHUNK_ERROR_PATTERN =
    /ChunkLoadError|Loading chunk .+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

interface CompiledVersionInfo {
    buildNumber?: string;
    buildId?: string;
    environment?: string;
    datetime?: string;
    pullRequestId?: string;
    checkForUpdates?: boolean;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent extends CodaltComponent implements OnInit {
    compiledVersion: CompiledVersionInfo = compiledVersionData || {};
    private tokenRefreshPending = false;

    toolboxWarn = false;
    showToolbox = false;
    toolbox: Toolbox;
    toolboxLoading = false;
    updateAvailable = false;
    print = false;

    private updateDialogOpen = false;
    private versionCheckInProgress = false;

    parentUser: User;
    user: User;
    window = window;
    /** Build is older than the latest PR commit */
    outdated = true;
    lastCommitDate: string;

    public constructor(
        private router: Router,
        private dialog: MatDialog,
        private authService: AuthService,
        private dialogEscapeService: DialogEscapeService,
        private confirmDialogService: ConfirmDialogService,
        private toolboxService: ToolboxService,
        private entitytypeService: EntityTypeService,
        private websocketService: WebsocketService,
        private ngSelectConfig: NgSelectConfig,
        private overlayContainer: OverlayContainer,
        private clientDeviceService: ClientDeviceService,
        private apiService: ApiService
    ) {
        super();

        const container = this.overlayContainer.getContainerElement();

        container.addEventListener('contextmenu', (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;

            // Prevent only on Material backdrop
            if (target?.classList.contains('cdk-overlay-backdrop')) {
                event.preventDefault();
            }
        });

        this.ngSelectConfig.notFoundText = 'Geen items gevonden';
        this.ngSelectConfig.loadingText = 'Aan het laden...';
        this.ngSelectConfig.clearAllText = 'Alles wissen';
        this.ngSelectConfig.typeToSearchText = 'Typ om te zoeken';
        this.ngSelectConfig.fixedPlaceholder = true;
        this.dialogEscapeService.init();
    }

    ngOnInit() {
        this.entitytypeService.writeEntityTypesToDb();
        this.print = location.href.indexOf('print') !== -1;
        if (!this.print) {
            this.clientDeviceService.register();
        }
        if (location.pathname === '/' && LocalStorage.getUserToken()) {
            this.authService.redirectAfterLogin();
        }

        this.checkLastCommit();

        this.checkToken();
        if (LocalStorage.getTokenDate()) {
            this.getToolbox();
        }
        setInterval(() => this.checkToken(), 24 * 60 * 60 * 1000); // covers tabs that stay open without navigating

        if (typeof document !== 'undefined' && document.addEventListener) {
            // Chromium Page Lifecycle API: fires only for a truly suspended tab, not every tab switch.
            document.addEventListener('freeze', () => this.websocketService.disconnect());
            document.addEventListener('resume', () => {
                this.websocketService.disconnect();
                this.checkToken();
            });
        }

        if (typeof window !== 'undefined' && window.addEventListener) {
            // Firefox/Safari equivalent: bfcache restore instead of Page Lifecycle freeze/resume.
            window.addEventListener('pageshow', (event: PageTransitionEvent) => {
                if (event.persisted) {
                    this.websocketService.disconnect();
                    this.checkToken();
                }
            });
        }

        this.subscriptions.add(
            this.authService.loggedIn.subscribe(loggedIn => {
                if (loggedIn) {
                    if (!this.print) {
                        this.clientDeviceService.register();
                    }
                    this.getToolbox();
                    this.checkLastCommit();
                    this.parentUser = LocalStorage.parentUser;
                    this.user = LocalStorage.getUser();
                    this.websocketService.disconnect();
                }
            })
        );

        this.parentUser = LocalStorage.parentUser;
        this.user = LocalStorage.getUser();

        this.subscriptions.add(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationError && this.isChunkError(event.error)) {
                    this.notifyUpdateAvailable();
                }
                if (event instanceof NavigationEnd) {
                    this.checkToken();
                }
            })
        );

        window.addEventListener('unhandledrejection', event => {
            if (this.isChunkError(event.reason)) {
                this.notifyUpdateAvailable();
            }
        });

        window.addEventListener(
            'error',
            event => {
                const target = event.target;
                const isReleaseAsset =
                    (target instanceof HTMLScriptElement && target.src.includes('/frontend-releases/')) ||
                    (target instanceof HTMLLinkElement && target.href.includes('/frontend-releases/'));

                if (
                    isReleaseAsset ||
                    (event instanceof ErrorEvent && this.isChunkError(event.error ?? event.message))
                ) {
                    this.notifyUpdateAvailable();
                }
            },
            true
        );

        window.addEventListener('focus', () => this.checkVersion());

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.checkVersion();
                this.checkLastCommit();
            }
        });

        this.checkVersion();

        this.subscriptions.add(
            timer(VERSION_CHECK_INTERVAL_MS, VERSION_CHECK_INTERVAL_MS).subscribe(() => this.checkVersion())
        );
    }

    backToParentUser() {
        this.router.navigateByUrl('/logout');
    }

    getToolbox() {
        if (
            !this.toolbox &&
            !this.toolboxLoading &&
            AuthorisationService.hasFunctionality(this.Functionalities.Toolbox)
        ) {
            this.toolboxLoading = true;
            this.subscriptions.add(
                timer(TOOLBOX_LOAD_DELAY_MS)
                    .pipe(switchMap(() => this.toolboxService.getCurrentToolbox()))
                    .subscribe(toolbox => {
                        this.toolbox = toolbox.data;
                        if (this.toolbox) {
                            this.toolboxWarn =
                                Utils.getTimeOrNull(this.toolbox.view_before) < Utils.getTimeOrNull(new Date());
                            this.showToolbox = !this.toolbox.read;
                        }
                    })
            );
        }
    }

    viewToolbox() {
        this.dialog
            .open(ToolboxDialogComponent, {
                panelClass: 'toolbox-dialog',
                width: '99vw',
                maxWidth: '1300px',
                height: '99vh',
                minHeight: '500px',
                maxHeight: '99vh',
                disableClose: true,
                data: this.toolbox,
            })
            .afterClosed()
            .subscribe(read => {
                this.toolbox.read = read;
                this.showToolbox = !this.toolbox.read;
            });
    }

    close() {
        this.showToolbox = false;
    }

    showUpdateDialog() {
        if (this.updateDialogOpen) {
            return;
        }

        this.updateDialogOpen = true;

        this.confirmDialogService
            .confirm({
                title: 'Nieuwe versie beschikbaar',
                text: 'Er is een nieuwe versie van de applicatie beschikbaar. Werk de applicatie bij om zonder onderbrekingen verder te werken.',
                confirm: 'Bijwerken',
                cancel: 'Annuleren',
            })
            .then(() => window.location.reload())
            .catch(() => undefined)
            .finally(() => (this.updateDialogOpen = false));
    }

    private checkLastCommit() {
        const pullRequestId = this.compiledVersion.pullRequestId;
        if (!this.environment.test || !pullRequestId || !LocalStorage.getUserToken()) {
            this.outdated = false;
            return;
        }

        this.apiService.getCallData$<{ lastCommitDate: string }>('pullrequest/last-update').subscribe({
            next: lastUpdate => {
                this.lastCommitDate = lastUpdate.lastCommitDate;
                this.outdated = new Date(this.compiledVersion.datetime) < new Date(lastUpdate.lastCommitDate);
            },
        });
    }

    private checkVersion() {
        if (this.versionCheckInProgress || this.updateAvailable || !this.compiledVersion.buildId) {
            return;
        }

        this.versionCheckInProgress = true;

        fetch('/assets/version.json?_=' + Date.now(), { cache: 'no-store' })
            .then(response => (response.ok ? response.json() : null))
            .then((version: CompiledVersionInfo | null) => {
                if (version?.checkForUpdates && version.buildId !== this.compiledVersion.buildId) {
                    this.notifyUpdateAvailable();
                }
            })
            .catch(() => undefined)
            .finally(() => (this.versionCheckInProgress = false));
    }

    private isChunkError(error: unknown): boolean {
        return CHUNK_ERROR_PATTERN.test(error instanceof Error ? error.name + ': ' + error.message : String(error));
    }

    private notifyUpdateAvailable() {
        this.updateAvailable = true;

        this.showUpdateDialog();
    }

    private checkToken() {
        const refreshNotIfBefore = new Date();
        refreshNotIfBefore.setDate(refreshNotIfBefore.getDate() - 3);
        const isStale = () => {
            const tokenDate = LocalStorage.getTokenDate();
            return !!tokenDate && new Date(tokenDate) < refreshNotIfBefore;
        };

        // checkToken runs on every navigation; without this guard each one chains another refresh
        if (!isStale() || this.tokenRefreshPending) {
            return;
        }
        this.tokenRefreshPending = true;

        // Re-check inside: another tab may have refreshed while waiting for the lock
        const refresh = async () => {
            try {
                if (!isStale()) {
                    return;
                }
                await firstValueFrom(this.authService.refresh());
                this.websocketService.disconnect();
            } catch (error) {
                // 500 already shows a dialog via CodaltInterceptor
                if (!(error instanceof ApiErrorResponse) || !error.message || error.status === 500) {
                    this.authService.logout();
                    return;
                }
                this.confirmDialogService
                    .confirm({
                        title: 'Uitgelogd',
                        text: error.message,
                        confirm: 'Opnieuw inloggen',
                        cancel: null,
                    })
                    .then(() => this.authService.logout());
            } finally {
                this.tokenRefreshPending = false;
            }
        };

        // Lock serializes tabs; unavailable in insecure contexts and old browsers
        setTimeout(
            () => (navigator.locks ? navigator.locks.request('token-refresh', refresh) : refresh()),
            (2 + Math.random() * 3) * 1000
        );
    }
}
